import React, {useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  InputAdornment,
  Pagination,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {ArrowBack, Check, Flight, FlightTakeoff, Group, PersonAdd, Search, WarningAmber} from '@mui/icons-material';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import {useLocation} from 'react-router-dom';
import {IFlight, IPassenger} from '../../store/apis/interfaces/flight';
import {useGetFlownPaxMutation} from '../../store/apis/ccs';
import useFadeNavigate from './useFadeNavigate';
import FlightStatusBadge from './FlightStatusBadge';

const fmt = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '—';

const ataStaMinutes = (flight: IFlight): number => {
  const sta = flight.arrival?.scheduledTime;
  const ata = flight.arrival?.actualTime;
  if (!sta || !ata) return 0;
  return Math.max(0, Math.round(DateTime.fromISO(ata).diff(DateTime.fromISO(sta), 'minutes').minutes));
};

const PAGE_SIZE = 20;

interface LocationState {
  flight: IFlight;
  carrier: string;
  flightNumber: string;
  date: string;
  origin: string;
}

const emptyForm = {paxGivenName: '', paxSurname: '', pnr: '', ticketNumber: '', boardingTime: ''};

const SATA_BLUE = '#0082BB';
const SATA_BLUE_DARK = '#005A8E';

const FlightPassengersPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const storageKey = state ? `pax-${state.carrier}-${state.flightNumber}-${state.date}` : null;

  const [getFlownPax, {data: apiPassengers, isLoading}] = useGetFlownPaxMutation();
  const [manualPassengers, setManualPassengers] = useState<IPassenger[]>(() => {
    if (!storageKey) return [];
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw).manual ?? []) : [];
    } catch { return []; }
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({paxGivenName: false, pnr: false});
  const restoredRef = useRef(false);

  useEffect(() => {
    if (state?.carrier && state?.flightNumber && state?.date && state?.origin) {
      getFlownPax({
        carrier: state.carrier,
        flightNumber: state.flightNumber,
        date: state.date,
        origin: state.origin
      });
    }
  }, []);

  // Restore selection once API passengers arrive
  useEffect(() => {
    if (!apiPassengers || restoredRef.current || !storageKey) return;
    restoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const ids: Array<{n: string; s: string | null; p: string}> = JSON.parse(raw).selectedIds ?? [];
      if (!ids.length) return;
      const all = [...apiPassengers, ...manualPassengers];
      const newSel = new Set<number>();
      all.forEach((pax, idx) => {
        if (ids.some(id => id.n === pax.paxGivenName && id.s === (pax.paxSurname ?? null) && id.p === pax.pnr)) {
          newSel.add(idx);
        }
      });
      if (newSel.size > 0) setSelected(newSel);
    } catch {}
  }, [apiPassengers]);

  // Persist selection and manual passengers whenever they change
  useEffect(() => {
    if (!restoredRef.current || !storageKey) return;
    const all = [...(apiPassengers ?? []), ...manualPassengers];
    const selectedIds = all
      .filter((_, idx) => selected.has(idx))
      .map(p => ({n: p.paxGivenName, s: p.paxSurname ?? null, p: p.pnr}));
    sessionStorage.setItem(storageKey, JSON.stringify({selectedIds, manual: manualPassengers}));
  }, [selected, manualPassengers]);

  if (!state?.flight) {
    return (
      <Box sx={{textAlign: 'center', mt: 4}}>
        <Typography color='error' gutterBottom>
          {t`No flight data. Please go back and search again.`}
        </Typography>
        <Button
          variant='text'
          startIcon={<ArrowBack />}
          onClick={() => navigate('/declarations/flight')}
          sx={{mt: 1, color: 'text.secondary'}}
        >
          {t`Back to Search`}
        </Button>
      </Box>
    );
  }

  const {flight, carrier, flightNumber, date} = state;

  const formattedDate = DateTime.fromFormat(date, 'yyyy-MM-dd').toLocaleString(DateTime.DATE_FULL);

  const allDelays = [
    ...(flight.departure.delays ?? []),
    ...(flight.arrival.delays ?? [])
  ].filter((d, i, arr) => arr.findIndex(x => x.code === d.code) === i);

  const hasDelay = ataStaMinutes(flight) > 0;
  const isCancelled = flight.status?.toUpperCase().includes('CANCEL') ?? false;

  const apiCount = (apiPassengers ?? []).length;
  const allPassengers = [...(apiPassengers ?? []), ...manualPassengers];
  const indexedPassengers = allPassengers.map((p, idx) => ({...p, _idx: idx, _manual: idx >= apiCount}));

  const sortedPassengers = [...indexedPassengers].sort((a, b) => {
    const pnrA = a.pnr || '';
    const pnrB = b.pnr || '';
    if (pnrA !== pnrB) {
      if (!pnrA) return 1;
      if (!pnrB) return -1;
      return pnrA.localeCompare(pnrB);
    }
    const sA = (a.paxSurname ?? '').toLowerCase();
    const sB = (b.paxSurname ?? '').toLowerCase();
    if (sA !== sB) return sA.localeCompare(sB);
    return a.paxGivenName.toLowerCase().localeCompare(b.paxGivenName.toLowerCase());
  });

  const matchesSearch = (p: (typeof indexedPassengers)[number]) =>
    !search ||
    p.paxGivenName.toLowerCase().includes(search.toLowerCase()) ||
    (p.paxSurname ?? '').toLowerCase().includes(search.toLowerCase()) ||
    p.pnr.toLowerCase().includes(search.toLowerCase());

  // Selected passengers always pinned at top, never paginated
  const selectedRows = sortedPassengers.filter((p) => selected.has(p._idx));

  // Unselected passengers matching search, paginated
  const unselectedRows = sortedPassengers.filter((p) => !selected.has(p._idx) && matchesSearch(p));
  const pageCount = Math.ceil(unselectedRows.length / PAGE_SIZE);
  const pagedUnselected = unselectedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hasPnrData = sortedPassengers.some(p => !!p.pnr);

  const groupByPnr = (passengers: typeof sortedPassengers) => {
    const map = new Map<string, typeof sortedPassengers>();
    for (const p of passengers) {
      const key = p.pnr || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return [...map.entries()];
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    const allVisible = [...selectedRows, ...unselectedRows];
    const allVisibleSelected = allVisible.every((p) => next.has(p._idx));
    if (allVisibleSelected) {
      allVisible.forEach((p) => next.delete(p._idx));
    } else {
      allVisible.forEach((p) => next.add(p._idx));
    }
    setSelected(next);
  };

  const toggle = (idx: number) => {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelected(next);
  };

  const openDialog = () => {
    setForm(emptyForm);
    setFormErrors({paxGivenName: false, pnr: false});
    setDialogOpen(true);
  };

  const handleAddPassenger = () => {
    const errors = {
      paxGivenName: !form.paxGivenName.trim(),
      pnr: !form.pnr.trim()
    };
    setFormErrors(errors);
    if (errors.paxGivenName || errors.pnr) return;

    const newIdx = allPassengers.length;
    const newPassenger: IPassenger = {
      paxGivenName: form.paxGivenName.trim(),
      paxSurname: form.paxSurname.trim() || null,
      pnr: form.pnr.trim().toUpperCase(),
      seatNumber: null,
      ticketNumber: form.ticketNumber.trim() || null,
      couponNumber: null,
      flyerId: null,
      destination: null,
      boardingTime: form.boardingTime.trim() || null
    };

    setManualPassengers((prev) => [...prev, newPassenger]);
    setSelected((prev) => new Set([...prev, newIdx]));
    setDialogOpen(false);
  };

  const selectedPassengers = allPassengers.filter((_, idx) => selected.has(idx));
  const missingTicketNumbers = isCancelled && selectedPassengers.some(p => !p.ticketNumber);

  const PassengerRow = ({passenger}: {passenger: (typeof indexedPassengers)[number]}) => {
    const isSelected = selected.has(passenger._idx);
    const initials = [passenger.paxGivenName?.[0], passenger.paxSurname?.[0]].filter(Boolean).join('').toUpperCase() || '?';
    return (
      <Card
        variant='outlined'
        sx={{
          cursor: 'pointer',
          borderRadius: 2,
          bgcolor: isSelected ? 'rgba(0,130,187,0.08)' : 'background.paper',
          borderColor: isSelected ? SATA_BLUE : 'divider',
          borderWidth: isSelected ? 2 : 1,
          transition: 'all 0.15s ease',
          '&:hover': {borderColor: SATA_BLUE, bgcolor: 'rgba(0,130,187,0.05)'},
        }}
        onClick={() => toggle(passenger._idx)}
      >
        <CardContent sx={{py: 1.25, px: 2, '&:last-child': {pb: 1.25}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${SATA_BLUE} 0%, ${SATA_BLUE_DARK} 100%)`,
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '0.85rem',
              boxShadow: isSelected ? `0 3px 8px ${SATA_BLUE_DARK}55` : '0 2px 5px rgba(0,0,0,0.18)',
              transition: 'box-shadow 0.15s ease',
            }}>
              {isSelected ? <Check sx={{fontSize: '1.2rem'}} /> : initials}
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                <Typography fontWeight='bold' variant='body2' noWrap
                  sx={{color: isSelected ? SATA_BLUE : 'text.primary'}}>
                  {passenger.paxSurname ? `${passenger.paxSurname}, ${passenger.paxGivenName}` : passenger.paxGivenName}
                </Typography>
                {passenger._manual && (
                  <Chip label={t`Manual`} size='small' variant='outlined' color='info'
                    sx={{fontSize: '0.65rem', height: 18, flexShrink: 0}} />
                )}
                {!passenger.ticketNumber && (
                  <Tooltip title={t`No ticket number on record — required to later verify the declaration's authenticity.`}>
                    <Chip icon={<WarningAmber sx={{fontSize: '0.85rem'}} />} label={t`No ticket #`} size='small'
                      variant='outlined' color='warning'
                      sx={{fontSize: '0.65rem', height: 18, flexShrink: 0}} />
                  </Tooltip>
                )}
                {!passenger.boardingTime && (
                  <Tooltip title={t`No recorded boarding time — this passenger did not board the flight.`}>
                    <Chip icon={<WarningAmber sx={{fontSize: '0.85rem'}} />} label={t`No show`} size='small'
                      variant='outlined' color='error'
                      sx={{fontSize: '0.65rem', height: 18, flexShrink: 0}} />
                  </Tooltip>
                )}
              </Box>
              {passenger.ticketNumber && (
                <Typography variant='caption' color='text.disabled' noWrap
                  sx={{fontSize: '0.68rem', fontFamily: 'monospace'}}>
                  {passenger.ticketNumber}{passenger.couponNumber && `/${passenger.couponNumber}`}
                </Typography>
              )}
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0}}>
              {passenger.pnr && (
                <Typography variant='caption' sx={{
                  fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.05em',
                  color: isSelected ? SATA_BLUE : 'text.secondary',
                  lineHeight: 1,
                }}>
                  {passenger.pnr}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {/* Flight header */}
      <Card
        elevation={2}
        sx={{borderTop: '3px solid', borderColor: isCancelled ? 'error.main' : hasDelay ? 'warning.main' : 'primary.main'}}
      >
        <CardContent sx={{py: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Button
              size='small'
              variant='text'
              onClick={() => navigate('/declarations/flight')}
              sx={{color: 'text.secondary', minWidth: 0, p: 0.5}}
            >
              <ArrowBack />
            </Button>

            <Box sx={{
              background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
              color: 'white',
              borderRadius: 2,
              width: 48,
              height: 48,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(1,87,155,0.45)',
              gap: 0.25,
            }}>
              <Typography sx={{fontWeight: 900, fontSize: '0.9rem', letterSpacing: '0.1em', lineHeight: 1}}>
                {carrier.toUpperCase()}
              </Typography>
              <FlightTakeoff sx={{fontSize: '0.65rem', opacity: 0.75}} />
            </Box>

            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Typography variant='h6' fontWeight='bold' lineHeight={1.2}>
                  {carrier.toUpperCase()}{flightNumber}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Typography variant='body1' fontWeight='bold'>{flight.origin}</Typography>
                  <Flight sx={{fontSize: '1rem', color: 'text.secondary'}} />
                  <Typography variant='body1' fontWeight='bold'>{flight.destination}</Typography>
                </Box>
                <FlightStatusBadge status={flight.status} hasDelay={hasDelay} />
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {formattedDate}
                {flight.departure.scheduledTime && ` · STD: ${fmt(flight.departure.scheduledTime)}`}
                {flight.departure.actualTime && ` · ATD: ${fmt(flight.departure.actualTime)}`}
              </Typography>
            </Box>

            {hasDelay && allDelays.length > 0 && (
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  minWidth: 180,
                  flexShrink: 0
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 0.5}}
                >
                  {allDelays.length > 1 ? t`DELAY REASONS` : t`DELAY REASON`}
                </Typography>
                {allDelays.map((d) => (
                  <Box key={d.code} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Typography variant='body2' sx={{lineHeight: 1.8}}>
                      <Box component='span' sx={{color: 'primary.main', fontWeight: 700}}>{d.code}</Box>
                      {' – '}{d.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Passengers card */}
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          {/* Title row */}
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
              <Group sx={{color: 'text.secondary'}} />
              <Typography variant='h6' fontWeight='bold'>
                {t`Passengers`}
                {allPassengers.length > 0 && (
                  <Box component='span' sx={{color: 'text.secondary', fontWeight: 400, ml: 0.5}}>
                    ({allPassengers.length})
                  </Box>
                )}
              </Typography>
              {selected.size > 0 && (
                <Chip
                  label={`${selected.size} ${t`selected`}`}
                  size='small'
                  color='primary'
                  sx={{height: 22, fontSize: '0.72rem', fontWeight: 700}}
                />
              )}
            </Box>
            <Button variant='contained' size='small' startIcon={<PersonAdd />} onClick={openDialog}>
              {t`Add Passenger`}
            </Button>
          </Box>

          {/* Search + Select All */}
          <Box sx={{display: 'flex', gap: 1, mb: 2}}>
            <TextField
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t`Search by name or PNR...`}
              size='small'
              sx={{flex: 1}}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{fontSize: '1.1rem', color: 'text.disabled'}} />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant='outlined'
              size='small'
              onClick={toggleAll}
              disabled={allPassengers.length === 0}
              sx={{flexShrink: 0}}
            >
              {t`Select All`}
            </Button>
          </Box>

          {/* Loading */}
          {isLoading && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 5}}>
              <CircularProgress size={36} />
            </Box>
          )}

          {!isLoading && (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              {/* Selected passengers — always visible at top */}
              {hasPnrData
                ? groupByPnr(selectedRows).map(([pnr, group], gi) => (
                    <Box key={`sel-${pnr || gi}`}>
                      {pnr && (
                        <Typography variant='caption' color='text.secondary'
                          sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block',
                               px: 0.5, mb: 0.5, mt: gi > 0 ? 1.5 : 0}}>
                          {pnr}
                        </Typography>
                      )}
                      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                        {group.map(p => <PassengerRow key={p._idx} passenger={p} />)}
                      </Box>
                    </Box>
                  ))
                : selectedRows.map(p => <PassengerRow key={p._idx} passenger={p} />)
              }

              {/* Top continue button + divider between selected and unselected */}
              {selectedRows.length > 0 && unselectedRows.length > 0 && (
                <>
                  <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 0.5}}>
                    <Tooltip title={missingTicketNumbers ? t`One or more selected passengers have no ticket number on record, which is required to later verify the declaration's authenticity.` : ''}>
                      <span>
                        <Button
                          variant='contained'
                          color='primary'
                          size='small'
                          sx={{fontWeight: 600}}
                          disabled={missingTicketNumbers}
                          onClick={() =>
                            navigate(
                              isCancelled
                                ? '/declarations/flight/declaration/cancellation'
                                : '/declarations/flight/declaration',
                              {state: {flight, carrier, flightNumber, date, selectedPassengers}}
                            )
                          }
                        >
                          {t`Continue`}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                  <Divider sx={{my: 1}}>
                    <Typography variant='caption' color='text.secondary' sx={{px: 1}}>
                      {unselectedRows.length} {t`remaining`}
                    </Typography>
                  </Divider>
                </>
              )}

              {/* Paginated unselected rows */}
              {hasPnrData
                ? groupByPnr(pagedUnselected).map(([pnr, group], gi) => (
                    <Box key={`unsel-${pnr || gi}`}>
                      {pnr && (
                        <Typography variant='caption' color='text.secondary'
                          sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block',
                               px: 0.5, mb: 0.5, mt: gi > 0 ? 1.5 : 0}}>
                          {pnr}
                        </Typography>
                      )}
                      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                        {group.map(p => <PassengerRow key={p._idx} passenger={p} />)}
                      </Box>
                    </Box>
                  ))
                : pagedUnselected.map(p => <PassengerRow key={p._idx} passenger={p} />)
              }

              {/* Empty state */}
              {selectedRows.length === 0 && unselectedRows.length === 0 && (
                <Box sx={{textAlign: 'center', py: 5}}>
                  <Group sx={{fontSize: '2.5rem', color: 'text.disabled', mb: 1}} />
                  <Typography color='text.secondary' variant='body2'>
                    {t`No passengers found.`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Pagination */}
          {!isLoading && pageCount > 1 && (
            <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                color='primary'
                size='small'
              />
            </Box>
          )}
        </CardContent>

        <Divider />

        <Box sx={{display: 'flex', justifyContent: 'space-between', p: 2}}>
          <Button
            variant='text'
            startIcon={<ArrowBack />}
            onClick={() => navigate('/declarations/flight')}
            sx={{color: 'text.secondary'}}
          >
            {t`Back to Search`}
          </Button>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5}}>
            {missingTicketNumbers && (
              <Typography variant='caption' color='error'>
                {t`One or more selected passengers have no ticket number on record.`}
              </Typography>
            )}
            <Tooltip title={missingTicketNumbers ? t`Required to later verify the declaration's authenticity.` : ''}>
              <span>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={selected.size === 0 || missingTicketNumbers}
                  sx={{fontWeight: 600}}
                  onClick={() =>
                    navigate(
                      isCancelled
                        ? '/declarations/flight/declaration/cancellation'
                        : '/declarations/flight/declaration',
                      {state: {flight, carrier, flightNumber, date, selectedPassengers}}
                    )
                  }
                >
                  {t`Continue`}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Card>

      {/* Add Passenger Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{fontWeight: 700}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <PersonAdd sx={{color: 'text.secondary'}} />
            {t`Add Passenger`}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{pt: 3}}>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <TextField
                label={t`Surname`}
                value={form.paxSurname}
                onChange={(e) => setForm((f) => ({...f, paxSurname: e.target.value}))}
                helperText={t`Last name (e.g. Doe)`}
                fullWidth
                size='small'
                autoFocus
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                label={t`Given Name`}
                value={form.paxGivenName}
                onChange={(e) => {
                  setForm((f) => ({...f, paxGivenName: e.target.value}));
                  setFormErrors((fe) => ({...fe, paxGivenName: false}));
                }}
                error={formErrors.paxGivenName}
                helperText={formErrors.paxGivenName ? t`Given name is required` : t`First name (e.g. John)`}
                fullWidth
                size='small'
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                label='PNR'
                value={form.pnr}
                onChange={(e) => {
                  setForm((f) => ({...f, pnr: e.target.value}));
                  setFormErrors((fe) => ({...fe, pnr: false}));
                }}
                error={formErrors.pnr}
                helperText={formErrors.pnr ? t`PNR is required` : t`Booking reference (e.g. AZR123)`}
                fullWidth
                size='small'
                inputProps={{style: {textTransform: 'uppercase'}}}
              />
            </Grid2>
            <Grid2 size={isCancelled ? 12 : 6}>
              <TextField
                label={t`Ticket Number`}
                value={form.ticketNumber}
                onChange={(e) => setForm((f) => ({...f, ticketNumber: e.target.value}))}
                helperText={t`Optional (e.g. 153-1234567890)`}
                fullWidth
                size='small'
              />
            </Grid2>
            {!isCancelled && (
              <Grid2 size={6}>
                <TextField
                  label={t`Boarding Time`}
                  value={form.boardingTime}
                  onChange={(e) => setForm((f) => ({...f, boardingTime: e.target.value}))}
                  helperText={t`Optional`}
                  fullWidth
                  size='small'
                  type='time'
                  InputLabelProps={{shrink: true}}
                />
              </Grid2>
            )}
          </Grid2>
        </DialogContent>
        <Divider />
        <DialogActions sx={{px: 3, py: 2, gap: 1}}>
          <Button variant='text' sx={{color: 'text.secondary'}} onClick={() => setDialogOpen(false)}>
            {t`Cancel`}
          </Button>
          <Button variant='contained' onClick={handleAddPassenger} startIcon={<PersonAdd />}>
            {t`Add Passenger`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlightPassengersPage;
